import { useEffect, useState } from "react"

import { Helmet } from "react-helmet"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Button, Card, Container, Grid, Input, Loader } from "semantic-ui-react"
import { useThrottle } from "simplydo/react/hooks"

import api from "api"
import GroupCard, { GroupCardListView } from "components/lib/Cards/GroupCard"
import { OrgSwitcherDropdown } from "components/lib/OrgSwitcherTray"
import { EmptyBox } from "components/lib/UI"
import { useAppSelector } from "store"
import useTheme from "theme/useTheme"
import util from "utils/utils"

const Groups = () => {
  const [yourGroups, setYourGroups] = useState([])
  const [discoverGroups, setDiscoverGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchInput, setSearchInput] = useState("")
  const theme = useTheme()
  const { t } = useTranslation()
  const user = useAppSelector((state) => state.user)

  const getGroups = useThrottle(
    () => {
      api.journey.record("groups", null, "viewGroupsPage")
      setLoading(true)
      const searchFilter = (g) => {
        const groupName = (g.name || "").toLowerCase()
        return !searchInput || groupName.includes(searchInput.toLowerCase())
      }
      api.groups.discover(
        (groups) => {
          setYourGroups(groups.filter((g) => searchFilter(g) && g.isInGroup))
          setDiscoverGroups(groups.filter((g) => searchFilter(g) && !g.isInGroup))
          setLoading(false)
        },
        (err) => {
          toast.error(err.message)
          setLoading(false)
        },
      )
    },
    400,
    [searchInput],
    { useLeadingCall: true },
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: Should refetch groups when user organisation changes
  useEffect(() => {
    getGroups()
  }, [getGroups, user?.ownerOrganisation?._id])

  return (
    <div>
      <Helmet title="Groups" />
      <div style={{ marginTop: 20 }}>
        <Container>
          <Grid stackable>
            <Grid.Column computer={11}>
              <h1 style={{ color: "#3b3b3b", margin: "0px", textTransform: "capitalize" }}>{t("generic.groups")}</h1>
              {user?.ownerOrganisations?.length > 1 ? (
                <p style={{ fontSize: "0.9rem" }}>
                  in <span style={{ fontWeight: 600 }}>{user.ownerOrganisation.name}</span>
                  {" - "}
                  <OrgSwitcherDropdown
                    pointing="top left"
                    basic
                    trigger={
                      <a tabIndex={0} role="button" onClick={(e) => e.preventDefault()}>
                        Change organisation
                      </a>
                    }
                  />
                </p>
              ) : null}
            </Grid.Column>
            <Grid.Column computer={5} textAlign="right" verticalAlign="bottom">
              {util.hasPermission(user, "org.createGroups", user.ownerOrganisation._id) && (
                <Button
                  size={theme?.sizes?.isMobile ? "tiny" : "medium"}
                  floated="right"
                  as={Link}
                  to="/groups/new"
                  icon="plus"
                  content={t("groups.discover.create")}
                />
              )}
            </Grid.Column>
          </Grid>
        </Container>
      </div>
      <Container style={{ marginTop: "1rem" }}>
        {loading && <Loader active />}
        <Input
          theme={theme}
          value={searchInput}
          placeholder={t("groups.search")}
          icon="search"
          style={{ width: 370 }}
          onChange={(_e, { value }) => setSearchInput(value)}
        />
        {!loading && !yourGroups.length && !discoverGroups.length ? (
          <EmptyBox>
            <h2>{searchInput.length ? t("groups.groups.noneTerm") : t("groups.groups.none")}</h2>
          </EmptyBox>
        ) : null}
        <Grid
          style={{ marginTop: "1rem" }}
          columns={yourGroups.length && discoverGroups.length && theme.sizes.isComputer ? 2 : 1}
        >
          <Grid.Row>
            {yourGroups.length ? (
              <Grid.Column size={8}>
                <h2 style={{ marginBottom: 20 }}>{t("groups.groups.member")}</h2>
                {yourGroups.map((group) => (
                  <GroupCardListView key={group._id} group={group} />
                ))}
              </Grid.Column>
            ) : null}
            {discoverGroups.length ? (
              <Grid.Column size={8} style={!theme.sizes.isComputer ? { marginTop: 20 } : {}}>
                <h2 style={{ marginBottom: 20 }}>{t("groups.discover.title")}</h2>
                <Card.Group itemsPerRow={theme.sizes.isMobile ? 1 : 2}>
                  {discoverGroups.map((group) => (
                    <GroupCard key={group._id} group={group} />
                  ))}
                </Card.Group>
              </Grid.Column>
            ) : null}
          </Grid.Row>
        </Grid>
      </Container>
    </div>
  )
}

export default Groups
